import { FilterInput } from '@kinetic-ui/data-layer';
import { FieldConfig } from '@qfa/forms';
import { useLatest } from '@qfa/react-components';
import { UseFormData, useForm } from 'kineticUtils';
import { isEqual } from 'lodash';
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PREFERENCE_KEYS, SetState } from '../../../types';
import { FilterConfig } from '../filterConfigs';
import { useToFilters } from '../hooks/useToFilters';
import { useToForms } from '../hooks/useToForms';
import { TransformConfig, TransformFactory } from '../transforms';

type FiltersContextValue = {
  clearActiveSavedFilter: boolean;
  filterConfigs: FilterConfig[];
  filterInputs: FilterInput[];
  filtersInitialized: boolean;
  filtersPreferenceKey: PREFERENCE_KEYS;
  form: UseFormData<any>;
  formLoaded: boolean;
  onFilterInputsChange: SetState<FilterInput[]>;
  populateForm: (filterInputs: FilterInput[]) => void;
  setFiltersInitialized: SetState<boolean>;
  setFormLoaded: SetState<boolean>;
  setClearActiveSavedFilter: Dispatch<boolean>;
  toFilters: (values: any) => FilterInput[];
  toForm: (values: FilterInput[]) => any;
};

export const FiltersContext = createContext<FiltersContextValue>({
  clearActiveSavedFilter: false,
  setClearActiveSavedFilter: () => {},
  filterConfigs: [],
  filtersPreferenceKey: '' as any,
  filterInputs: [],
  onFilterInputsChange: () => {},
  form: {} as any,
  populateForm: () => {},
  formLoaded: false,
  setFormLoaded: () => {},
  toFilters: () => [],
  toForm: () => ({}),
  filtersInitialized: false,
  setFiltersInitialized: () => {},
});
export const useFiltersContext = () => {
  return useContext(FiltersContext);
};

export const FiltersContextProvider = ({
  children,
  filterConfigs,
  filtersPreferenceKey,
  onFilterInputsChange: _onChange,
}: PropsWithChildren<{
  filtersPreferenceKey?: PREFERENCE_KEYS;
  filterConfigs: FilterConfig[];
  onFilterInputsChange: SetState<FilterInput[]>;
}>) => {
  const [filterInputs, setFilterInputs] = useState<FilterInput[]>([]);
  const [formLoaded, setFormLoaded] = useState(false);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const latestReady = useLatest(formLoaded);

  const onFilterInputsChange = useCallback(
    (newState: SetStateAction<FilterInput[]>) => {
      setFilterInputs((prev) => {
        // update our local state, but also give it to the user.
        const nextState = typeof newState === 'function' ? newState(prev) : newState;
        _onChange(nextState);
        return nextState;
      });
    },
    [_onChange]
  );

  const { fields, transforms } = useMemo(() => {
    const newFields: FieldConfig[] = [];
    const newTransforms: Record<
      string,
      { transformConfig?: TransformConfig<any, any>; transformer: TransformFactory }
    > = {};

    filterConfigs.forEach(({ field, transform, transformer }) => {
      newFields.push(field);

      let transformKey = transform?.filterField ?? field.name;
      /** Some filters have more than one field that use the same filterField
       * We need to be able to look up the transformers using the FilterInput
       * so if the filterInputOperator attribute is set, we'll append it to the transformKey
       */
      if (transform?.filterInputOperator) {
        transformKey += `__${transform.filterInputOperator}`;
      }

      newTransforms[transformKey] = {
        transformConfig: transform,
        transformer: transformer({ transform, field }),
      };
    });
    return {
      fields: newFields,
      transforms: newTransforms,
    };
  }, [filterConfigs]);

  const toFilters = useToFilters({ transforms });
  const populatedFilters = useRef<null | FilterInput[]>(null);

  const onFormChange = useCallback(
    (values) => {
      if (!latestReady.current) {
        setFormLoaded(true);
      }

      return values;
    },
    [latestReady]
  );

  const form = useForm({
    fields,
    onChange: onFormChange,
  });

  const {
    values: [values],
  } = form;
  const [clearActiveSavedFilter, setClearActiveSavedFilter] = useState(false);

  useEffect(
    () => {
      if (!formLoaded || !filtersInitialized) {
        return;
      }
      const newFilters = toFilters(values);
      if (isEqual(filterInputs, newFilters)) {
        return;
      }

      onFilterInputsChange(newFilters);
      if (populatedFilters.current && !isEqual(populatedFilters.current, newFilters)) {
        setClearActiveSavedFilter(true);
      }
    },
    // only fire on value change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values]
  );

  const toForm = useToForms({ transforms });

  const markFormPopulated = useCallback((filterInputs: FilterInput[]) => {
    populatedFilters.current = filterInputs;
  }, []);

  const contextValue = useMemo(
    () => ({
      filtersPreferenceKey: filtersPreferenceKey ?? ('' as any),
      filterConfigs,
      onFilterInputsChange,
      filterInputs,
      form,
      populateForm: markFormPopulated,
      formLoaded: formLoaded,
      setFormLoaded: setFormLoaded,
      clearActiveSavedFilter,
      setClearActiveSavedFilter,
      toFilters,
      toForm,
      filtersInitialized,
      setFiltersInitialized,
    }),
    [
      filtersPreferenceKey,
      filterConfigs,
      onFilterInputsChange,
      filterInputs,
      form,
      markFormPopulated,
      formLoaded,
      clearActiveSavedFilter,
      toFilters,
      toForm,
      filtersInitialized,
    ]
  );

  return <FiltersContext.Provider value={contextValue}>{children}</FiltersContext.Provider>;
};
